'use client';

import * as React from 'react';
import { useRoomContext } from '@livekit/components-react';
import { useOptionalHostSettings } from '@/lib/HostSettingsContext';
import { playRecordingStartFailedAlert } from '@/lib/notificationSound';
import { publishRecordingEvent } from '@/lib/recordingEvents';
import { isHostRole, resolveRoleFromMetadata } from '@/lib/participantRoles';
import { RECORDING_START_FAILED_EVENT } from '@/lib/recording-start-failed';
import { startRecordingFromRoom } from '@/lib/recording-status-client';
import {
  RECORDING_RETRY_BUTTON_LABEL,
  RECORDING_START_FAILED_BODY,
  RECORDING_START_FAILED_DISMISS,
  RECORDING_START_FAILED_TITLE,
} from '@/lib/recording-ui-messages';

/**
 * Aviso para todos na sala quando a gravação não chega a começar.
 * A borda amarela some; este cartão fica até alguém clicar.
 */
export function RecordingStartFailedNotice() {
  const room = useRoomContext();
  const [open, setOpen] = React.useState(false);
  const [message, setMessage] = React.useState(RECORDING_START_FAILED_BODY);
  const [busy, setBusy] = React.useState(false);
  const wasOpenRef = React.useRef(false);

  const hostSettings = useOptionalHostSettings();
  const canRetry = hostSettings
    ? hostSettings.isHost
    : isHostRole(resolveRoleFromMetadata(room?.localParticipant?.metadata));

  React.useEffect(() => {
    const onFailed = (event: Event) => {
      const detail = (event as CustomEvent<{ message?: string }>).detail;
      setMessage(detail?.message?.trim() || RECORDING_START_FAILED_BODY);
      setOpen(true);
    };
    const onRecovered = () => setOpen(false);

    window.addEventListener(RECORDING_START_FAILED_EVENT, onFailed);
    window.addEventListener('recording:start', onRecovered);
    return () => {
      window.removeEventListener(RECORDING_START_FAILED_EVENT, onFailed);
      window.removeEventListener('recording:start', onRecovered);
    };
  }, []);

  React.useEffect(() => {
    if (open && !wasOpenRef.current) {
      playRecordingStartFailedAlert();
    }
    wasOpenRef.current = open;
  }, [open]);

  const handleRetry = async () => {
    if (!canRetry || !room?.name || busy) return;
    setBusy(true);
    try {
      const result = await startRecordingFromRoom(room);
      if (result.httpStatus === 202 || result.state === 'preparing') {
        window.dispatchEvent(new CustomEvent('recording:preparing'));
        publishRecordingEvent(room, 'preparing', { queuePosition: result.queuePosition });
        setOpen(false);
        return;
      }
      if (result.httpStatus >= 400) {
        setMessage(RECORDING_START_FAILED_BODY);
        setOpen(true);
        return;
      }
      const confirmed = result.state === 'active' && result.isRecordingFile;
      window.dispatchEvent(new CustomEvent(confirmed ? 'recording:start' : 'recording:starting'));
      setOpen(false);
    } catch {
      setMessage(RECORDING_START_FAILED_BODY);
      setOpen(true);
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="recording-start-failed"
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="recording-start-failed-title"
      aria-describedby="recording-start-failed-body"
    >
      <button
        type="button"
        className="recording-start-failed__backdrop"
        aria-label="Fechar aviso"
        onClick={() => setOpen(false)}
      />
      <div className="recording-start-failed__card">
        <h2 id="recording-start-failed-title" className="recording-start-failed__title">
          {RECORDING_START_FAILED_TITLE}
        </h2>
        <p id="recording-start-failed-body" className="recording-start-failed__body">
          {message}
        </p>
        <div className="recording-start-failed__actions">
          {canRetry ? (
            <button
              type="button"
              className="recording-start-failed__retry"
              onClick={() => {
                void handleRetry();
              }}
              disabled={busy}
            >
              {busy ? 'Tentando…' : RECORDING_RETRY_BUTTON_LABEL}
            </button>
          ) : null}
          <button
            type="button"
            className="recording-start-failed__dismiss"
            onClick={() => setOpen(false)}
          >
            {RECORDING_START_FAILED_DISMISS}
          </button>
        </div>
      </div>
    </div>
  );
}
